import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import isEqual from 'lodash-es/isEqual';
import Toggle from 'react-toggle';

import intlShape from '@/shared/utils/intlShape';
import DataCacheService from '@/shared/services/DataCacheService';
import userStorage from '@/shared/services/userStorage';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import RelationSearch from '@/shared/components/input/RelationSearch';
import YearButtonGroup from '@/shared/components/YearButtonGroup';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';
import DelayedInput from '@/shared/components/input/DelayedInput';
import { parseIntOrNull } from '@/shared/utils/adminpulse';
import authService, { privileges } from '@/shared/services/AuthService';
import config from '../../../config';
import api from '../../../shared/api';
import messages from './budgetMessages';
import DynamicFilter, {
  getDynamicFiltersFromDefaultFilters,
  handleDynamicFiltersChanged,
  initDynamicFilters,
  resetDynamicFilters,
} from '../../../shared/components/DynamicFilter';

const currentYear = moment.utc().year();
const yearFrom = currentYear - 3;
const yearUntil = currentYear + 1;

export const defaultFilters = {
  associate: null, // UserId
  assistant: null, // UserId
  assistantBackup: null, // UserId
  responsible: null, // UserId
  billingUser: null, // UserId
  seniorUser: null, // UserId
  relationId: null, // String
  relationName: null,
  relationType: [], // Array
  withLabels: [], // Array
  withoutLabels: [], // Array,
  ownCompanies: [],
  assignmentTemplateId: null,
  invoiceScheduleTypes: [],
  endFiscalYears: [],
  year: moment.utc().year(),
  withBudget: null,
  budgetAmountUsed: null,
  budgetHoursUsed: null,
  includeInactive: false,
  relationGroups: [],
  filterBehaviours: {
    withLabels: 1,
  },
};
const allDynamicFilters = [
  ...getDynamicFiltersFromDefaultFilters(defaultFilters),
  {
    id: 'budgetAmountUsed',
    label: messages.BudgetAmountUsedPct,
    enabled: () =>
      authService.hasPermission({
        functionality: 'pricelist',
        privilege: privileges.Read,
      }),
  },
  { id: 'budgetHoursUsed', label: messages.BudgetHoursUsedPct },
  { id: 'includeInactive', label: messages.IncludeInactiveRelations },
];

const dynamicFiltersStorageKey = 'BudgetOverviewDynamicFilters';

class BudgetOverviewFilters extends Component {
  state = {
    dynamicFilters: [],
    labels: [],
  };
  static propTypes = {
    countSelected: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    filters: PropTypes.shape({
      responsible: PropTypes.string,
      assistant: PropTypes.string,
      assistantBackup: PropTypes.string,
      associate: PropTypes.string,
      billingUser: PropTypes.string,
      seniorUser: PropTypes.string,
      relationId: PropTypes.string,
      relationName: PropTypes.string,
      relationType: PropTypes.arrayOf(PropTypes.number),
      withLabels: PropTypes.arrayOf(PropTypes.string),
      withoutLabels: PropTypes.arrayOf(PropTypes.string),
      ownCompanies: PropTypes.arrayOf(PropTypes.string),
      assignmentTemplateId: PropTypes.string,
      invoiceScheduleTypes: PropTypes.arrayOf(PropTypes.string),
      year: PropTypes.number,
      withBudget: PropTypes.bool,
      budgetAmountUsed: PropTypes.number,
      budgetHoursUsed: PropTypes.number,
      includeInactive: PropTypes.bool,
      relationGroups: PropTypes.arrayOf(PropTypes.string),
    }),
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    filters: defaultFilters,
  };

  componentDidMount() {
    const { intl, filters } = this.props;
    this.assignmentsEnabled = DataCacheService.assignmentsEnabled();
    const labelPromise = api.base.get(`${config.appUrls.api}/api/Resource/Tags`);

    Promise.all([labelPromise]).then(([{ data: labels }]) => {
      this.setState({
        labels: labels
          .map(x => ({ id: x.id, label: x.name, sort: x.name.toLowerCase() }))
          .sort(dynamicSort('sort')),
        dynamicFilters: initDynamicFilters(
          intl,
          allDynamicFilters,
          userStorage.getItem(dynamicFiltersStorageKey) || [],
          filters,
          defaultFilters,
        ),
      });
    });
  }

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };
    if (filters !== newFilters) onFilterChange(newFilters);
  };
  resetFilters = () => {
    this.filterChanged(defaultFilters);
  };
  dynamicFilterChanged = (item, selected) => {
    this.setState(({ dynamicFilters }) => {
      const { filters } = this.props;
      const newFilters = handleDynamicFiltersChanged(
        dynamicFilters,
        item,
        selected,
        filters,
        defaultFilters,
      );
      userStorage.setItem(
        dynamicFiltersStorageKey,
        newFilters.filter(x => x.selected).map(x => x.id),
      );
      if (
        !selected &&
        item.id !== 'selectAll' &&
        !isEqual(filters[item.id], defaultFilters[item.id])
      ) {
        // Reset filter when it's removed
        this.filterChanged({ [item.id]: defaultFilters[item.id] });
      }
      return { dynamicFilters: newFilters };
    });
  };
  savedFilterChanged = filters => {
    const { onFilterChange } = this.props;
    const newFilters = { ...defaultFilters, ...filters };
    if (typeof newFilters.dateFrom === 'string') newFilters.dateFrom = moment.utc(filters.dateFrom);
    if (typeof newFilters.dateUntil === 'string')
      newFilters.dateUntil = moment.utc(filters.dateUntil);

    onFilterChange(newFilters);

    this.setState(({ dynamicFilters }) => ({
      dynamicFilters: resetDynamicFilters(dynamicFilters, newFilters, defaultFilters),
    }));
  };
  relationChanged = item => {
    if (!item || !item.id) {
      this.filterChanged({ relationId: null, relationName: null });
    } else {
      this.filterChanged({
        relationId: item.key,
        relationName: item.label,
      });
    }
  };
  withBudgetChanged = checked => {
    this.filterChanged({ withBudget: checked ? true : null });
  };
  withoutBudgetChanged = checked => {
    this.filterChanged({ withBudget: checked ? false : null });
  };
  renderDynamicFilter = ({ type, filters, intl }) => {
    switch (type) {
      case 'budgetAmountUsed': {
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <div className="input-group" title={intl.formatMessage(messages.BudgetAmountUsedPct)}>
                <DelayedInput
                  id="BudgetAmountUsed"
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  value={filters.budgetAmountUsed}
                  onChange={value =>
                    this.filterChanged({ budgetAmountUsed: parseIntOrNull(value) })
                  }
                  className="form-control"
                  placeholder={intl.formatMessage(messages.BudgetAmountUsedPct)}
                />
                <span className="input-group-addon">%</span>
              </div>
            </div>
          </div>
        );
      }
      case 'budgetHoursUsed': {
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <div className="input-group" title={intl.formatMessage(messages.BudgetHoursUsedPct)}>
                <DelayedInput
                  id="BudgetHoursUsed"
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  value={filters.budgetHoursUsed}
                  onChange={value => this.filterChanged({ budgetHoursUsed: parseIntOrNull(value) })}
                  className="form-control"
                  placeholder={intl.formatMessage(messages.BudgetHoursUsedPct)}
                />
                <span className="input-group-addon">%</span>
              </div>
            </div>
          </div>
        );
      }
      case 'includeInactive':
        return (
          <div className="col-lg-4 col-sm-6">
            <div className="form-group">
              <label className="react-toggle-wrapper margin5T">
                <Toggle
                  checked={filters.includeInactive}
                  onChange={e => this.filterChanged({ includeInactive: !!e.target.checked })}
                />
                <FormattedMessage className="label-text" {...messages.IncludeInactiveRelations} />
              </label>
            </div>
          </div>
        );
      default:
        console.warn(`Missing filter: ${type}`);
        return null;
    }
  };

  render() {
    const { dynamicFilters, labels } = this.state;
    const { intl, filters, countSelected } = this.props;
    //

    let initRelation = null;
    if (filters.relationId) {
      initRelation = {
        key: filters.relationId,
        label: filters.relationName,
      };
    }
    const visibleFilters = dynamicFilters.filter(x => x.selected).map(x => x.id);

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-lg-4">
            <div className="form-group">
              <RelationSearch
                onChange={this.relationChanged}
                initialSelectedItem={initRelation}
                includeInactive={filters.includeInactive}
              />
            </div>
          </div>
          <div className="col-lg-4 col-md-6">
            <YearButtonGroup
              value={filters.year}
              yearFrom={yearFrom}
              yearUntil={yearUntil}
              showAll
              onChange={year => this.filterChanged({ year })}
            />
          </div>
        </div>
        <div className="row">
          {visibleFilters && visibleFilters.length > 0
            ? visibleFilters.map(id => (
                <DynamicFilter
                  key={id}
                  type={id}
                  intl={intl}
                  labels={labels}
                  filters={filters}
                  filterChanged={this.filterChanged}
                  relationUserSearchParams={{ withUnassigned: false }}
                  renderCustomFilter={this.renderDynamicFilter}
                />
              ))
            : null}
          <div className="col-lg-4">
            <div className="form-group flex-row">
              <label className="react-toggle-wrapper margin5T margin10R flex-col">
                <Toggle
                  checked={filters.withBudget === true}
                  onChange={e => this.withBudgetChanged(e.target.checked)}
                />
                <FormattedMessage className="label-text" {...messages.WithBudget} />
              </label>
              <label className="react-toggle-wrapper margin5T margin10R flex-col">
                <Toggle
                  checked={filters.withBudget === false}
                  onChange={e => this.withoutBudgetChanged(e.target.checked)}
                />
                <FormattedMessage className="label-text" {...messages.WithoutBudget} />
              </label>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <SavedFiltersButton
              screen="BudgetOverview"
              currentFilters={filters}
              defaultFilters={defaultFilters}
              onFiltersChanged={this.savedFilterChanged}
              dynamicFilters={dynamicFilters}
              onDynamicFiltersChanged={this.dynamicFilterChanged}
            />
            {countSelected && <div className="pull-right margin10T">{countSelected}</div>}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default injectIntl(BudgetOverviewFilters);
